import { motion } from "framer-motion";
import { ArrowRight, Users, Building2, Video, ShieldCheck, UserCheck, FileWarning, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

export default function HLLanding() {
  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-950/90 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
          <span className="font-bold tracking-tight text-xl text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            The Human<span className="text-emerald-400">InLoop</span>
          </span>
          <div className="flex items-center gap-3">
            <Link to="/humanloop/aspirant" className="text-sm text-slate-300 hover:text-emerald-400 transition-colors" data-testid="hl-nav-aspirant">I'm a Professional</Link>
            <Link to="/humanloop/corporate" className="bg-emerald-500 text-slate-950 px-5 py-2 rounded-full text-sm font-bold hover:bg-emerald-400 transition-all" data-testid="hl-nav-corporate">I'm Hiring</Link>
          </div>
        </div>
      </header>

      {/* Hero — The Problem We Solve */}
      <section className="py-20 md:py-28 relative" data-testid="hl-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-2 mb-6">
              <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400">Real People. Real Mentorship. Real Hiring.</span>
            </motion.div>

            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white leading-[1.1]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              In a world where AI writes CVs, we bring the{" "}
              <span className="text-emerald-400">human back into hiring.</span>
            </motion.h1>

            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="text-base md:text-lg text-slate-400 mt-6 leading-relaxed max-w-2xl">
              The hiring world has a problem — AI-generated CVs perfectly match job descriptions, making it impossible to distinguish real talent from polished fiction. Meanwhile, young professionals in Tier 2 & 3 cities lack access to real-world mentorship that prepares them for what corporates actually need.
            </motion.p>

            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="text-base text-slate-300 mt-4 leading-relaxed max-w-2xl font-medium">
              We fix both. Our platform pairs professionals with mentors who sharpen their real skills, then lets them prove themselves through a 2-minute video pitch — not a keyword-stuffed PDF. Corporates see the actual person before they invest a single interview hour.
            </motion.p>
          </div>

          {/* Problem-Solution cards */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-14">
            {[
              { icon: FileWarning, title: "The Problem", desc: "AI-generated CVs game ATS filters. 78% of recruiters can't tell real skills from AI-polished profiles anymore.", color: "text-red-400", bg: "bg-red-500/5 border-red-500/20" },
              { icon: UserCheck, title: "Our Solution", desc: "Real mentorship + authentic 2-min video pitches. You see the person — their communication, confidence, and clarity — before you hire.", color: "text-emerald-400", bg: "bg-emerald-500/5 border-emerald-500/20" },
              { icon: ShieldCheck, title: "Verified & Real", desc: "Employer emails are OTP-verified. Aspirant profiles are mentor-reviewed. No fake profiles, no AI-generated content — just real people.", color: "text-cyan-400", bg: "bg-cyan-500/5 border-cyan-500/20" },
            ].map((c, i) => (
              <div key={i} className={`rounded-2xl border p-6 ${c.bg}`}>
                <c.icon className={`w-6 h-6 ${c.color} mb-3`} strokeWidth={1.5} />
                <h3 className="text-base font-semibold text-white mb-2">{c.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Demo Video Section */}
      <section className="py-16 bg-slate-900/30" data-testid="hl-demo-video">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-8">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-3">See How It Works</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Watch a 2-Minute Platform Demo</h2>
          </div>
          <div className="aspect-video rounded-2xl overflow-hidden bg-slate-900 border border-slate-800 relative">
            {/* Replace this src with your actual demo video */}
            <div className="w-full h-full flex flex-col items-center justify-center text-center p-8">
              <Video className="w-16 h-16 text-emerald-400/30 mb-4" />
              <p className="text-lg font-semibold text-slate-300">Demo Video Coming Soon</p>
              <p className="text-sm text-slate-500 mt-2 max-w-md">Record a quick walkthrough of the aspirant profile creation and corporate talent discovery experience, and paste the YouTube/Loom link here.</p>
              <p className="text-xs text-slate-600 mt-4 font-mono bg-slate-800/50 px-3 py-1.5 rounded">Edit: HLLanding.jsx → replace video placeholder</p>
            </div>
          </div>
        </div>
      </section>

      {/* Two Tiles — Aspirant & Employer */}
      <section className="py-20" data-testid="hl-portal-tiles">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-3">Get Started</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Choose Your Portal</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Aspirant Tile */}
            <Link to="/humanloop/aspirant" data-testid="hl-tile-aspirant">
              <motion.div whileHover={{ y: -4 }} className="bg-slate-900/50 border border-slate-800 rounded-3xl p-8 h-full hover:border-emerald-500/30 transition-all group cursor-pointer">
                <div className="w-14 h-14 bg-emerald-500/10 rounded-2xl flex items-center justify-center mb-6 border border-emerald-500/20 group-hover:bg-emerald-500/20 transition-all">
                  <Users className="w-7 h-7 text-emerald-400" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>For Professionals</h3>
                <p className="text-sm text-slate-400 leading-relaxed mb-6">
                  0–15 years experience? Build your authentic profile, record a 2-minute video pitch, get mentored by industry veterans, and get discovered by top employers — without relying on AI-written CVs.
                </p>
                <ul className="space-y-2 mb-6">
                  {["Create your real profile & video pitch", "Answer 3 questions that reveal who you truly are", "Get mentorship to sharpen your pitch", "Be discovered by verified employers", "Free to join — minimal fees for mentoring sessions"].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-slate-400">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <span className="inline-flex items-center gap-2 text-emerald-400 font-medium text-sm group-hover:gap-3 transition-all">
                  Create Your Profile <ArrowRight size={16} />
                </span>
              </motion.div>
            </Link>

            {/* Employer Tile */}
            <Link to="/humanloop/corporate" data-testid="hl-tile-corporate">
              <motion.div whileHover={{ y: -4 }} className="bg-slate-900/50 border border-slate-800 rounded-3xl p-8 h-full hover:border-emerald-500/30 transition-all group cursor-pointer">
                <div className="w-14 h-14 bg-emerald-500/10 rounded-2xl flex items-center justify-center mb-6 border border-emerald-500/20 group-hover:bg-emerald-500/20 transition-all">
                  <Building2 className="w-7 h-7 text-emerald-400" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>For Employers</h3>
                <p className="text-sm text-slate-400 leading-relaxed mb-6">
                  Skip the resume lottery. Browse real professionals with video pitches — see their communication, confidence, and cultural fit before you invest a single interview hour. Only verified official email IDs accepted.
                </p>
                <ul className="space-y-2 mb-6">
                  {["OTP-verified official email registration only", "Browse pre-vetted talent with 2-min video pitches", "Filter by skills, experience, location & availability", "Shortlist candidates and view detailed profiles", "See the real person, not an AI-generated resume"].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-slate-400">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center gap-2 text-emerald-400 font-medium text-sm group-hover:gap-3 transition-all">
                    Start Hiring <ArrowRight size={16} />
                  </span>
                  <span className="text-[10px] text-slate-600 border border-slate-800 px-2 py-0.5 rounded-full flex items-center gap-1">
                    <ShieldCheck size={10} /> OTP Verified
                  </span>
                </div>
              </motion.div>
            </Link>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-slate-900/30">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Sparkles className="w-8 h-8 text-emerald-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Real mentorship. Real people. Real hiring.
          </h2>
          <p className="text-sm text-slate-400 mb-8 max-w-lg mx-auto">
            In a world where things change overnight and AI can fake everything on paper — the only thing that still matters is the real person behind the profile.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/humanloop/aspirant" className="bg-emerald-500 text-slate-950 px-8 py-3 rounded-full font-bold hover:bg-emerald-400 transition-all text-sm">I'm a Professional</Link>
            <Link to="/humanloop/corporate" className="bg-transparent text-white px-8 py-3 rounded-full font-bold border border-slate-700 hover:border-emerald-500 hover:text-emerald-400 transition-all text-sm">I'm an Employer</Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 border-t border-slate-900">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-xs text-slate-600">The HumanInLoop — A product by <a href="/" className="text-cyan-400 hover:text-cyan-300">BharatIntelliAI</a> (Rewat Intelliforge Technologies LLP)</p>
          <p className="text-xs text-slate-700 mt-2">thehumaninloop.co.in</p>
        </div>
      </footer>
    </div>
  );
}
